import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { logout } from '../authentication/store/authentication.action';
import { User } from '../authentication/model/user.model';
import { updateActiveRole, updateUserLanguage } from '../authentication/store/user.action';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { selectComponentNavRoot } from '../router/store/router-store.selector';
import { selectHasPolicy } from '../authentication/store/user.selector';
import { getEnvironmentConfig } from '../environment-config/store/environment-config.action';
import { selectIsNotProdEnv, selectShortEnvName } from '../environment-config/store/environment-config.selector';
import { environment } from '../../environments/environment';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { ActiveRoleEnum } from '../model/activeRoleEnum';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatChipAvatar } from '@angular/material/chips';

@Component({
  selector: 'sibat-navigation-rail',
  template: `
    <div class="navigation-rail-container" [class.not-prod]="isNotProdEnv$ | async">
      <nav class="navigation-rail mat-elevation-z4">
        <div class="navigation-rail-top">
          <a routerLink="/" class="navigation-rail-logo"><img src="assets/logo.svg" alt="{{ 'layout.nav.brand' | transloco }}" /></a>
          <h1 class="navigation-rail-title">SIBAT</h1>
          @if (isNotProdEnv$ | async) {
          <div class="env-config">{{ envName$ | async }}</div>
          }
          <span class="navigation-rail-language">
            <a (click)="selectLanguage('fr')" [ngClass]="{ active: (lang$ | async) === 'fr' }">FR</a>
            <a (click)="selectLanguage('de')" [ngClass]="{ active: (lang$ | async) === 'de' }">DE</a>
            @if (isAdmin$ | async) {
            <a (click)="selectLanguage('keys')" [ngClass]="{ active: (lang$ | async) === 'keys' }">keys</a>
            }
          </span>
          <button
            mat-icon-button
            [matMenuTriggerFor]="menu"
            [matTooltip]="user ? user.firstName + ' - ' + mapActiveRoleToEnum(user.activeRole) : 'User not available'"
            matTooltipPosition="right"
          >
            <mat-icon>person</mat-icon>
          </button>
          <mat-menu #menu="matMenu">
            @if (user?.roles?.length > 0) { @for (role of user?.roles; track role) {
            <button mat-menu-item (click)="changeRole(role.code)">
              <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                <span>{{ role.name }}</span>
                @if (user?.activeRole === role.code) {
                <mat-icon matChipAvatar style="margin-left: 10px">check</mat-icon>
                }
              </div>
            </button>
            } } @else {
            <button mat-menu-item disabled>
              <span>No roles available</span>
            </button>
            }
          </mat-menu>
        </div>
        <div class="navigation-rail-middle">
          <div class="nav-button">
            <button
              mat-icon-button
              class="navigation-rail-button"
              [ngClass]="{ selected: 'cockpit' === (navRoot$ | async) }"
              [disabled]="user === null"
              routerLink="/cockpit"
            >
              <mat-icon>dashboard</mat-icon>
            </button>
            <span class="visually-hidden">{{ 'layout.nav.cockpit' | transloco }}</span>
            <div class="mobile-only">{{ 'layout.nav.cockpit' | transloco }}</div>
          </div>
          <div class="nav-button">
            <button
              mat-icon-button
              class="navigation-rail-button"
              [ngClass]="{ selected: 'map' === (navRoot$ | async) }"
              [disabled]="user === null"
              routerLink="/"
            >
              <mat-icon>map</mat-icon>
            </button>
            <span class="visually-hidden">{{ 'layout.nav.map' | transloco }}</span>
            <div class="mobile-only">{{ 'layout.nav.map_short' | transloco }}</div>
          </div>
          <!--          <ng-container *ngIf="sinboardUrl$ | async as sinboardUrl">-->
          <!--            <div class="nav-button" *ngIf="isECABOrAbove$ | async">-->
          <!--              <a-->
          <!--                mat-icon-button-->
          <!--                class="navigation-rail-button"-->
          <!--                target="_blank"-->
          <!--                rel="noopener noreferrer"-->
          <!--                [href]="sinboardUrl"-->
          <!--              >-->
          <!--                <mat-icon>thunderstorm</mat-icon>-->
          <!--              </a>-->
          <!--              <div class="mobile-only">Sinboard</div>-->
          <!--            </div>-->
          <!--          </ng-container>-->
          <div class="nav-button mobile-only">
            <button mat-icon-button [matMenuTriggerFor]="menu" class="navigation-rail-button">
              <mat-icon>more_vert</mat-icon>
            </button>
            <div>{{ 'layout.nav.more' | transloco }}</div>
          </div>
          <mat-menu #menu="matMenu">
            <button mat-menu-item [matMenuTriggerFor]="languages" class="mobile-only">
              <mat-icon>language</mat-icon>
              <span>{{ 'cockpit.language' | transloco }}</span>
            </button>
            @if (user) {
            <button mat-menu-item class="mobile-only" (click)="openHelp()">
              <mat-icon>open_in_new</mat-icon>
              <span>{{ 'layout.nav.help' | transloco }}</span>
            </button>
            } @if (user) {
            <button mat-menu-item class="mobile-only" (click)="createIssue()">
              <mat-icon>open_in_new</mat-icon>
              <span>feedback</span>
            </button>
            }
            <button mat-menu-item class="mobile-only" (click)="logout()">
              <mat-icon>power_settings_new</mat-icon>
              <span>{{ 'layout.nav.logout' | transloco }}</span>
            </button>
          </mat-menu>
          <mat-menu #languages="matMenu">
            <button mat-menu-item class="mobile-only" (click)="selectLanguage('de')">
              <span>DE</span>
            </button>
            <button mat-menu-item class="mobile-only" (click)="selectLanguage('fr')">
              <span>FR</span>
            </button>
            @if (isAdmin$ | async) {
            <button mat-menu-item class="mobile-only" (click)="selectLanguage('keys')">
              <span>keys</span>
            </button>
            }
          </mat-menu>
        </div>
        <div class="navigation-rail-bottom">
          @if (user) {
          <a class="navigation-rail-button link-button" mat-button data-testid="help-link" (click)="openHelp()"
            >{{ 'layout.nav.help' | transloco }}
            <mat-icon class="open-in-new">open_in_new</mat-icon>
          </a>
          <a class="navigation-rail-button link-button" mat-button data-testid="feedback-link" (click)="createIssue()"
            >feed<br />back
            <mat-icon class="open-in-new">open_in_new</mat-icon>
          </a>
          }
          <button mat-button [disabled]="!user" class="navigation-rail-button" (click)="logout()">
            <mat-icon>power_settings_new</mat-icon>
            <span class="visually-hidden">{{ 'layout.nav.logout' | transloco }}</span>
          </button>
        </div>
      </nav>
      <main class="navigation-rail-content" #content>
        <ng-content></ng-content>
      </main>
    </div>
  `,
  styleUrls: ['navigation-rail.component.scss'],
  imports: [
    RouterLink,
    NgClass,
    MatIconButton,
    MatMenuTrigger,
    MatTooltip,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatChipAvatar,
    MatButton,
    TranslocoPipe,
    AsyncPipe,
  ],
})
export class NavigationRailComponent implements OnInit {
  @Input() user?: User;
  currentUrl: string;
  envName$ = this.store.select(selectShortEnvName);
  isNotProdEnv$ = this.store.select(selectIsNotProdEnv);
  isAdmin$ = this.store.select(selectHasPolicy('cockpit.management.get'));
  lang$ = this.translocoService.langChanges$;
  navRoot$ = this.store.select(selectComponentNavRoot);

  constructor(private store: Store, private translocoService: TranslocoService, private router: Router) {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed()).subscribe(() => {
      this.currentUrl = this.router.url;
    });
  }

  ngOnInit(): void {
    this.store.dispatch(getEnvironmentConfig());
  }

  logout() {
    this.store.dispatch(logout());
  }

  changeRole(role: string) {
    this.store.dispatch(updateActiveRole({ newRole: role }));
    if (!this.currentUrl.includes('/building/')) {
      window.location.reload();
    }
  }

  mapActiveRoleToEnum(role: string): ActiveRoleEnum | undefined {
    switch (role) {
      case 'role_cecb':
        return ActiveRoleEnum.cecb;
      case 'role_read_only':
        return ActiveRoleEnum.readOnly;
      case 'role_admin':
        return ActiveRoleEnum.admin;
      case 'role_api':
        return ActiveRoleEnum.api;
      case 'role_secretary':
        return ActiveRoleEnum.secretary;
      case 'role_expert':
        return ActiveRoleEnum.expert;
      case 'role_scpi':
        return ActiveRoleEnum.scpi;
      case 'role_administration_communal':
        return ActiveRoleEnum.administrationCommunal;
      default:
        return undefined;
    }
  }

  selectLanguage(language: string) {
    this.store.dispatch(updateUserLanguage({ language }));
  }

  openHelp = () => window.open(environment.support.helpLink, '_blank');

  createIssue = () => window.open(environment.support.feedbackLink, '_blank');
}
